import { HttpStatusCode } from "../structs";
import { Challenge, ValidatePhoneFunc } from "./challenge";
import { KeyCode } from "../enums";

export class SmspinChallenge extends Challenge {
    public static SMS_CODE_PHONE_REQUIRED = "phone_required";
    public static SMS_CODE_TTL_EXPIRED = "ttl_expired";
    public static SMS_CODE_PHONE_BLOCKED = "phone_blocked";

    private button_lock = 0;
    private static resend_tick_count = 0;
    private static resend_tick_handler: NodeJS.Timeout;

    static readonly TICK_DURATION = 1100;
    static readonly DEFAULT_RESEND_TIME = 60;

    private pin_verify_phone_input: HTMLInputElement;
    private text_phone_div: HTMLDivElement;

    // Default func for validating user inputted phone
    private validatePhone(phone: string): boolean {
        const valid = !(!phone || 0 === phone.length);
        if (!valid) {
            alert(this.core.conf.getLocalization().smsEnterPhoneInputError);
        }

        return valid;
    }

    // ValidatePhone function can be the default one or client provided
    private getValidatePhoneFunc(): ValidatePhoneFunc {
        if (this.core.conf.custom_validate_phone) {
            return this.core.conf.custom_validate_phone;
        }
        return this.validatePhone.bind(this);
    }

    private getPhoneFromWindow(): string {
        const phone: string =  (window as any).__challenger_conf?.p?.phone || ""; 
        return phone == "required" ? "" : phone;
    }

    // Checks user inputted phone and sets param value if valid
    private checkUserPhone(): boolean {
        const phone: string = this.pin_verify_phone_input.value;
        const valid: boolean = this.getValidatePhoneFunc()(phone);

        if (valid) {
            // This change is specifically for RPay integration using the customValidation function
            // Because they are changing the phone number during phone validation
            (this.core.conf.param as Record<string, unknown>)["phone"] = this.getPhoneFromWindow() || phone;
        }

        return valid;
    }

    private resendBtnHandler() {
        if ((this.pin_verify_phone_input == null || this.checkUserPhone()) && !this.button_lock) {
            this.button_lock = 1;
            this.core.loadChallenge.bind(this.core)();
        }
    }

    private resendBtnHandlerKey(event: KeyboardEvent) {
        if (event.key == KeyCode.Space || event.key == KeyCode.Enter) {
            this.resendBtnHandler();
        }
    }

    private resendTick(resendBtn: HTMLDivElement, resendTxt: HTMLDivElement): void {
        if (SmspinChallenge.resend_tick_count > 0) {
            resendBtn.classList.add("pin-disabled");
            resendBtn.classList.remove("pin-enabled");
            resendBtn.setAttribute("aria-label", this.format(this.core.conf.getLocalization().resendIn, [SmspinChallenge.resend_tick_count]));
            resendTxt.innerHTML = this.format(this.core.conf.getLocalization().resendIn, [SmspinChallenge.resend_tick_count]);

            SmspinChallenge.resend_tick_handler = setTimeout(function () {
                SmspinChallenge.resend_tick_count = SmspinChallenge.resend_tick_count - 1;
                this.resendTick(resendBtn, resendTxt);
            }.bind(this), SmspinChallenge.TICK_DURATION);
        } else {
            resendBtn.removeAttribute("aria-label");
            resendBtn.classList.remove("pin-disabled");
            resendBtn.classList.add("pin-enabled");
            resendBtn.onclick = this.resendBtnHandler.bind(this);
            resendBtn.onkeydown = this.resendBtnHandlerKey.bind(this);
            resendTxt.innerHTML = "";
        }
    }

    private createResendDivs(divPrefix: string, buttonLabel: string): [HTMLDivElement, HTMLDivElement, HTMLDivElement] {
        const resendBtnDiv: HTMLDivElement = document.createElement("div");
        const resendBtn: HTMLDivElement = document.createElement("div");
        const resendTxt: HTMLDivElement = document.createElement("div");

        resendBtnDiv.className = `${divPrefix}-box`;

        resendBtn.id = `${divPrefix}-btn`;
        resendBtn.className = `${divPrefix}-btn`;
        resendBtn.innerHTML = buttonLabel;
        resendBtn.tabIndex = 0;
        resendBtn.setAttribute("role", "button");

        resendTxt.id = `${divPrefix}-txt`;
        resendTxt.className = `${divPrefix}-txt`;
        
        resendBtnDiv.appendChild(resendBtn);
        resendBtnDiv.appendChild(resendTxt);

        return [resendBtnDiv, resendBtn, resendTxt];
    }

    protected async execute(): Promise<boolean> {
        if (!this.mdata && !(await this.getMedia())) { return false; }
        return this.processMdata();
    }

    async render(parentDiv: HTMLElement): Promise<boolean> {
        clearTimeout(SmspinChallenge.resend_tick_handler);

        this.button_lock = 0;

        if (this.cdata_status == HttpStatusCode.TOO_MANY_REQUESTS) {
            const thresholdText = document.createElement("div");
            thresholdText.innerText = this.core.conf.getLocalization().smsPinError;
            thresholdText.id = "pin-threshold-txt";
            thresholdText.className = "pin-threshold-txt";
            
            const [resendBtnDiv, resendBtn, resendTxt] = this.createResendDivs("pin-resend", this.core.conf.getLocalization().resendCode);
            
            SmspinChallenge.resend_tick_count = SmspinChallenge.DEFAULT_RESEND_TIME;
            this.resendTick(resendBtn, resendTxt);

            parentDiv.appendChild(thresholdText);
            parentDiv.appendChild(resendBtnDiv);
        } else if (this.cdata_status == HttpStatusCode.BAD_REQUEST && this.cdata.code == SmspinChallenge.SMS_CODE_PHONE_REQUIRED) {
            const verifyPhoneText = document.createElement("div");
            verifyPhoneText.innerText = this.core.conf.getLocalization().smsEnterPhone;
            verifyPhoneText.setAttribute("aria-label", this.core.conf.getLocalization().smsEnterPhone);
            verifyPhoneText.tabIndex = 0;
            verifyPhoneText.id = "pin-verifyphone-txt";
            verifyPhoneText.className = "pin-verifyphone-txt";

            const phoneHelpText = document.createElement("div");
            phoneHelpText.innerText = this.core.conf.getLocalization().phoneHelpMessage;
            phoneHelpText.id = "phonehelp-txt";
            phoneHelpText.className = "phonehelp-txt";

            const verifyPhoneInput = document.createElement("input");
            verifyPhoneInput.setAttribute("aria-label", this.core.conf.getLocalization().phoneHelpMessage);
            verifyPhoneInput.id = "pin-verifyphone-input";
            verifyPhoneInput.className = "pin-verifyphone-input";
            verifyPhoneInput.setAttribute("type", "tel");
            this.pin_verify_phone_input = verifyPhoneInput;

            const [sendBtnDiv, sendBtn, sendTxt] = this.createResendDivs("pin-send", this.core.conf.getLocalization().sendCode);
            this.resendTick(sendBtn, sendTxt);

            parentDiv.appendChild(verifyPhoneText);
            parentDiv.appendChild(phoneHelpText);
            parentDiv.appendChild(verifyPhoneInput);
            parentDiv.appendChild(sendBtnDiv);
        } else if (this.cdata_status == HttpStatusCode.BAD_REQUEST && this.cdata.code == SmspinChallenge.SMS_CODE_TTL_EXPIRED) {
            const ttlExpiredText = document.createElement("div");
            ttlExpiredText.innerText = this.core.conf.getLocalization().ttlExpired;
            ttlExpiredText.id = "ttl-expired-txt";
            ttlExpiredText.className = "ttl-expired-txt";
            ttlExpiredText.tabIndex = 0;
            ttlExpiredText.setAttribute("aria-label", this.core.conf.getLocalization().ttlExpired);
            parentDiv.appendChild(ttlExpiredText);
        } else if (this.cdata_status == HttpStatusCode.FORBIDDEN && this.cdata.code == SmspinChallenge.SMS_CODE_PHONE_BLOCKED) {
            const phoneBlockedText = document.createElement("div");
            phoneBlockedText.innerText = this.core.conf.getLocalization().phoneBlockedMessage;
            phoneBlockedText.id = "phone-blocked-txt";
            phoneBlockedText.className = "phone-blocked-txt";
            phoneBlockedText.tabIndex = 0;
            phoneBlockedText.setAttribute("aria-label", this.core.conf.getLocalization().phoneBlockedMessage);
            parentDiv.appendChild(phoneBlockedText);
        } else {
            const textPhone = document.createElement("div");
            textPhone.id = "pin-text";
            textPhone.className = "pin-text";
            textPhone.tabIndex = 0;
            this.text_phone_div = textPhone;

            const inputHelpDiv = document.createElement("div");
            inputHelpDiv.className = "pin-input-help";
            inputHelpDiv.innerHTML = this.core.conf.getLocalization().smsPinDescription;

            const edit = this.getCresElement();
            edit.className = "c-pin-input";
            edit.id = "c-pin-input";
            edit.type = "text";
            edit.setAttribute("aria-label", this.core.conf.getLocalization().smsPinDescription);
            edit.maxLength = 6;

            const textHelpSms = document.createElement("div");
            textHelpSms.id = "smshelp-txt";
            textHelpSms.className = "smshelp-txt";
            textHelpSms.innerText = this.core.conf.getLocalization().smsHelpMessage;
            textHelpSms.tabIndex = 0;
            textHelpSms.setAttribute("aria-label", this.core.conf.getLocalization().smsHelpMessage);

            const [resendBtnDiv, resendBtn, resendTxt] = this.createResendDivs("pin-resend", this.core.conf.getLocalization().resendCode);

            SmspinChallenge.resend_tick_count = SmspinChallenge.DEFAULT_RESEND_TIME;
            this.resendTick(resendBtn, resendTxt);

            parentDiv.appendChild(textPhone);
            parentDiv.appendChild(inputHelpDiv);
            parentDiv.appendChild(edit);
            parentDiv.appendChild(textHelpSms);
            parentDiv.appendChild(resendBtnDiv);

            if (!(await this.execute())) { return false; }
        }

        return true;
    }

    private modifyPhoneHandler() {
        (this.core.conf.param as Record<string, unknown>)["phone"] = "required";
        this.core.loadChallenge.bind(this.core)();
    }

    private modifyPhoneHandlerKey(event: KeyboardEvent) {
        if (event.key == " " || event.key == "Enter") {
            this.modifyPhoneHandler();
        }
    }

    protected processMdata(): boolean {
        if (this.mdata.match(/\*/)) {
            this.text_phone_div.innerHTML = this.core.conf.getLocalization().smsEnterPinLinkage;
            this.text_phone_div.setAttribute("aria-label", this.core.conf.getLocalization().smsEnterPinLinkage);
        } else {
            this.text_phone_div.innerHTML = this.core.conf.getLocalization().smsEnterPinNoLinkage;
            this.text_phone_div.setAttribute("aria-label", this.core.conf.getLocalization().smsEnterPinLinkage);
        }

        const textPhoneValue = document.createElement("div");
        textPhoneValue.id = "phone-value";
        textPhoneValue.className = "phone-value";
        textPhoneValue.tabIndex = 0;
        textPhoneValue.innerHTML = this.mdata;

        const modifyPhoneValue = document.createElement("div");
        modifyPhoneValue.id = "pin-modifyPhone-txt";
        modifyPhoneValue.className = "pin-modifyPhone-txt";
        modifyPhoneValue.innerHTML = "[" + this.core.conf.getLocalization().edit + "]";
        modifyPhoneValue.tabIndex = 0;
        modifyPhoneValue.setAttribute("role", "button");

        this.insertAfter(textPhoneValue, this.text_phone_div);
        this.insertAfter(modifyPhoneValue, textPhoneValue);

        modifyPhoneValue.onclick = this.modifyPhoneHandler.bind(this);
        modifyPhoneValue.onkeydown = this.modifyPhoneHandlerKey.bind(this);

        return true;
    }
}
